import React, { useState, useEffect } from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { Alert } from 'react-bootstrap';
import swal from 'sweetalert';

import PageTitle from './../../markup/Layout/PageTitle';
import { getPublicVacancyById, getPublicVacancies, applyToVacancy } from '../../services/VacanciesService';
import { saveJob, unsaveJob } from '../../services/SavedJobsService';
import { useAuthStore } from '../../store/authStore';
import { LogoSpinner } from '../../components/LogoSpinner';

var bnr = require('./../../images/banner/bnr1.jpg');

const formatSalary = (min, max) => {
    if (!min && !max) return 'No especificado';
    if (min && max) {
        return `RD$ ${min.toLocaleString('es-DO')} - RD$ ${max.toLocaleString('es-DO')}`;
    }
    if (min) {
        return `Desde RD$ ${min.toLocaleString('es-DO')}`;
    }
    if (max) {
        return `Hasta RD$ ${max.toLocaleString('es-DO')}`;
    }
    return 'No especificado';
};

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
};

const getRelationName = (relation) => {
    return relation?.nombre || 'N/A';
};

export function JobDetail() {
    const location = useLocation();
    const history = useHistory();

    const [vacancy, setVacancy] = useState(null);
    const [suggestedVacancies, setSuggestedVacancies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);
    const [error, setError] = useState(null);
    const [applying, setApplying] = useState(false);
    const [applySuccess, setApplySuccess] = useState(null);
    const [applyError, setApplyError] = useState(null);
    const [isSaved, setIsSaved] = useState(false);
    const [savingJob, setSavingJob] = useState(false);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const id = queryParams.get('id');

        if (id) {
            loadVacancy(id);
            loadSuggestedVacancies(id);
        } else {
            setError('No se proporcionó un ID de vacante válido.');
            setLoading(false);
        }
    }, [location.search]);

    const loadVacancy = async (id) => {
        try {
            setLoading(true);
            setError(null);
            const response = await getPublicVacancyById(id);
            setVacancy(response.data);
            // Usar el campo isSaved que viene del backend
            setIsSaved(response.data?.isSaved || false);
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Error al cargar la vacante. Por favor, intenta nuevamente.';
            setError(errorMessage);
            console.error('Error loading vacancy:', err);
        } finally {
            setLoading(false);
        }
    };

    const loadSuggestedVacancies = async (currentVacancyId) => {
        try {
            setLoadingSuggestions(true);
            const response = await getPublicVacancies({
                limit: 8,
                page: 1,
                orderBy: 'createdAt',
                orderDirection: 'desc'
            });

            const filtered = (response.data.data || []).filter(v => v.id !== currentVacancyId).slice(0, 8);
            setSuggestedVacancies(filtered);
        } catch (err) {
            console.error('Error loading suggested vacancies:', err);
            setSuggestedVacancies([]);
        } finally {
            setLoadingSuggestions(false);
        }
    };

    const handleBack = () => {
        history.push('/browse-job-filter-grid');
    };

    const { isAuthenticated, auth } = useAuthStore();
    const authenticated = isAuthenticated();
    const isCompany = auth.user?.tipoPerfil === 'EMPRESA';

    const handleApply = async () => {
        if (!authenticated) {
            history.push('/login');
            return;
        }

        // Verificar si es una empresa
        if (isCompany) {
            setApplyError('Las empresas no pueden postularse a vacantes. Esta función es solo para candidatos.');
            return;
        }

        setApplying(true);
        setApplyError(null);
        setApplySuccess(null);

        try {
            const res = await applyToVacancy(vacancy.id, 'Borrador');
            setApplySuccess('Borrador creado. Completa tu currículum para confirmar.');
            const applicationId = res?.data?.id || res?.data?.applicationId || res?.data?.jobApplicationId || null;
            if (applicationId) {
                history.push(`/my-resume?applicationId=${applicationId}`);
            } else {
                history.push('/applied-jobs');
            }
        } catch (err) {
            const msg = err.response?.data?.message || err.message || 'Error al postular a la vacante.';
            setApplyError(msg);
            console.error('Error applying to vacancy:', err);
        } finally {
            setApplying(false);
        }
    };

    const handleSaveToggle = async () => {
        if (!authenticated) {
            swal({
                title: 'Inicia sesión',
                text: 'Debes iniciar sesión para guardar vacantes',
                icon: 'info',
                buttons: {
                    cancel: 'Cancelar',
                    confirm: {
                        text: 'Iniciar sesión',
                        value: true
                    }
                }
            }).then((willLogin) => {
                if (willLogin) {
                    history.push('/login');
                }
            });
            return;
        }

        setSavingJob(true);

        try {
            if (isSaved) {
                // Si ya está guardada, eliminarla
                await unsaveJob(vacancy.id);
                setIsSaved(false);
                swal({
                    title: '¡Eliminado!',
                    text: 'La vacante ha sido eliminada de guardados',
                    icon: 'success',
                    timer: 2000,
                    buttons: false
                });
            } else {
                // Si no está guardada, guardarla
                await saveJob(vacancy.id);
                setIsSaved(true);
                swal({
                    title: '¡Guardado!',
                    text: 'La vacante ha sido guardada exitosamente',
                    icon: 'success',
                    timer: 2000,
                    buttons: false
                });
            }
        } catch (err) {
            const msg = err.response?.data?.message || err.message || 'Error al guardar la vacante.';
            swal({
                title: 'Error',
                text: msg,
                icon: 'error'
            });
            console.error('Error toggling save job:', err);
        } finally {
            setSavingJob(false);
        }
    };

    if (loading) {
        return <LogoSpinner />;
    }

    if (error || !vacancy) {
        return (
            <div className="page-content bg-white">
                <div className="dez-bnr-inr overlay-black-middle" style={{ backgroundImage: "url(" + bnr + ")" }}>
                    <PageTitle activeName="Detalle de la vacante" motherName="Inicio" />
                </div>
                <div className="content-block">
                    <div className="section-full bg-white p-t50 p-b20">
                        <div className="container">
                            <Alert variant="danger">
                                {error || 'No se pudo cargar la vacante. Por favor, verifica que el ID sea válido.'}
                            </Alert>
                            <div className="text-center m-t30">
                                <Link to="/browse-job-filter-grid" className="site-button">
                                    Volver a Búsqueda de Vacantes
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const locationText = `${getRelationName(vacancy.ciudad)}, ${getRelationName(vacancy.pais)}`;
    const salaryText = formatSalary(vacancy.salarioMinimo, vacancy.salarioMaximo);
    const experienceText = getRelationName(vacancy.tiempoExperienciaRequerido);
    const modalidadText = getRelationName(vacancy.modalidadTrabajo);
    const tipoTrabajoText = getRelationName(vacancy.tipoTrabajo);
    const tipoContratoText = getRelationName(vacancy.tipoContratoTrabajo);
    const categoriaText = getRelationName(vacancy.categoria);

    return (
        <>
            <div className="page-content bg-white">
                <div className="dez-bnr-inr overlay-black-middle" style={{ backgroundImage: "url(" + bnr + ")" }}>
                    <div className="container">
                        <div className="dez-bnr-inr-entry">
                            <div className="row">
                                <div className="col-12">
                                    <PageTitle activeName="Detalle de la vacante" motherName="Inicio" />
                                    <div className="m-t20">
                                        <button
                                            onClick={handleBack}
                                            className="site-button button-sm"
                                            style={{
                                                background: '#fff',
                                                color: '#009247',
                                                border: '1px solid #009247'
                                            }}
                                        >
                                            <i className="fa fa-arrow-left m-r5"></i> Volver a Búsqueda
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="content-block">
                    <div className="section-full content-inner-1">
                        <div className="container">
                            <div className="row">
                                <div className="col-lg-4">
                                    <div className="sticky-top">
                                        <div className="widget bg-white p-lr20 p-t20 widget_getintuch radius-sm">
                                            <h4 className="text-black font-weight-700 p-t10 m-b15">Detalle de la vacante</h4>
                                            <ul>
                                                <li className="">
                                                    <i className="ti-location-pin"></i>
                                                    <strong className="font-weight-700 text-black" style={{ fontSize: '15px' }}>Dirección</strong>
                                                    <span className="text-black-light">
                                                        {vacancy.direccion || 'No especificada'}
                                                    </span>
                                                </li>
                                                <li>
                                                    <i className="ti-location-pin"></i>
                                                    <strong className="font-weight-700 text-black" style={{ fontSize: '15px' }}>Ubicación</strong>
                                                    <span className="text-black-light">{locationText}</span>
                                                </li>
                                                <li>
                                                    <i className="ti-money"></i>
                                                    <strong className="font-weight-700 text-black" style={{ fontSize: '15px' }}>Salario</strong>
                                                    <span className="text-black-light">{salaryText}</span>
                                                </li>
                                                <li>
                                                    <i className="ti-shield"></i>
                                                    <strong className="font-weight-700 text-black" style={{ fontSize: '15px' }}>Experiencia</strong>
                                                    <span className="text-black-light">{experienceText}</span>
                                                </li>
                                                <li>
                                                    <i className="ti-bookmark"></i>
                                                    <strong className="font-weight-700 text-black" style={{ fontSize: '15px' }}>Modalidad</strong>
                                                    <span className="text-black-light">{modalidadText}</span>
                                                </li>
                                                <li>
                                                    <i className="ti-briefcase"></i>
                                                    <strong className="font-weight-700 text-black" style={{ fontSize: '15px' }}>Tipo de Trabajo</strong>
                                                    <span className="text-black-light">{tipoTrabajoText}</span>
                                                </li>
                                                <li>
                                                    <i className="ti-file"></i>
                                                    <strong className="font-weight-700 text-black" style={{ fontSize: '15px' }}>Tipo de Contrato</strong>
                                                    <span className="text-black-light">{tipoContratoText}</span>
                                                </li>
                                                <li>
                                                    <i className="ti-tag"></i>
                                                    <strong className="font-weight-700 text-black" style={{ fontSize: '15px' }}>Categoría</strong>
                                                    <span className="text-black-light">{categoriaText}</span>
                                                </li>
                                                {vacancy.inglesRequerido && (
                                                    <li>
                                                        <i className="ti-world"></i>
                                                        <strong className="font-weight-700 text-black" style={{ fontSize: '15px' }}>Idioma</strong>
                                                        <span className="text-black-light">Inglés Requerido</span>
                                                    </li>
                                                )}
                                                {vacancy.limiteAplicantes && (
                                                    <li>
                                                        <i className="ti-user"></i>
                                                        <strong className="font-weight-700 text-black" style={{ fontSize: '15px' }}>Límite de Aplicantes</strong>
                                                        <span className="text-black-light">
                                                            {vacancy.limiteAplicantes} aplicantes
                                                        </span>
                                                    </li>
                                                )}
                                                <li>
                                                    <i className="ti-calendar"></i>
                                                    <strong className="font-weight-700 text-black" style={{ fontSize: '15px' }}>Publicado</strong>
                                                    <span className="text-black-light">{formatDate(vacancy.createdAt)}</span>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-lg-8">
                                    <div className="job-info-box">
                                        <h3 className="m-t0 m-b10 font-weight-700 title-head">
                                            <span className="text-secondry m-r30" style={{ color: "#00004C" }}>
                                                {vacancy.titulo}
                                            </span>
                                        </h3>
                                        <ul className="job-info">
                                            <li>
                                                <i className="ti-location-pin text-black m-r5"></i>
                                                {locationText}
                                            </li>
                                            <li>
                                                <strong>Modalidad:</strong> {modalidadText}
                                            </li>
                                            <li>
                                                <strong>Tipo de Trabajo:</strong> {tipoTrabajoText}
                                            </li>
                                            <li>
                                                <strong>Salario:</strong> {salaryText}
                                            </li>
                                        </ul>

                                        {vacancy.tags && (
                                            <div className="p-t20">
                                                <strong>Etiquetas: </strong>
                                                {vacancy.tags.map((tag, index) => (
                                                    <span key={index} className="badge badge-secondary m-r5" style={{
                                                        background: '#f5f5f5',
                                                        color: '#666',
                                                        padding: '5px 10px',
                                                        borderRadius: '3px',
                                                        fontSize: '12px'
                                                    }}>
                                                        {tag.tag.nombre}
                                                    </span>
                                                ))}
                                            </div>
                                        )}

                                        {vacancy.descripcion && (
                                            <>
                                                <h5 className="font-weight-600 p-t30" style={{ marginTop: 20 }}>Descripción del puesto</h5>
                                                <div className="dez-divider divider-2px bg-gray-dark mb-4 mt-0"></div>
                                                <div
                                                    dangerouslySetInnerHTML={{ __html: vacancy.descripcion }}
                                                    style={{
                                                        color: '#212529',
                                                        fontSize: '14px',
                                                        lineHeight: '1.6',
                                                        wordBreak: 'break-word'
                                                    }}
                                                />
                                            </>
                                        )}

                                        <div className="p-t50">
                                            {applyError && (
                                                <Alert variant="danger" onClose={() => setApplyError(null)} dismissible>
                                                    {applyError}
                                                </Alert>
                                            )}
                                            {applySuccess && (
                                                <Alert variant="success" onClose={() => setApplySuccess(null)} dismissible>
                                                    {applySuccess}
                                                </Alert>
                                            )}
                                            
                                            {vacancy.isExpired && (
                                                <Alert variant="warning">
                                                    <i className="fa fa-exclamation-triangle m-r5"></i>
                                                    Esta vacante ha expirado y ya no acepta postulaciones.
                                                </Alert>
                                            )}

                                            <div className="d-flex gap-3">
                                                {!isCompany && !vacancy.isExpired && (
                                                    <button
                                                        type="button"
                                                        className="site-button"
                                                        onClick={handleApply}
                                                        disabled={applying || !!applySuccess}
                                                    >
                                                        {applying ? 'Postulando...' : applySuccess ? 'Postulado' : 'Postular a esta vacante'}
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    className="site-button"
                                                    onClick={handleSaveToggle}
                                                    disabled={savingJob}
                                                    style={{
                                                        background: isSaved ? '#dc3545' : '#009247',
                                                        borderColor: isSaved ? '#dc3545' : '#009247',
                                                        marginLeft: !isCompany && !vacancy.isExpired ? '10px' : '0'
                                                    }}
                                                >
                                                    {savingJob ? (
                                                        <>
                                                            <i className="fa fa-spinner fa-spin m-r5"></i>
                                                            {isSaved ? 'Quitando...' : 'Guardando...'}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className={`fa ${isSaved ? 'fa-heart' : 'fa-heart-o'} m-r5`}></i>
                                                            {isSaved ? 'Quitar de guardados' : 'Guardar vacante'}
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                            {isCompany && (
                                                <div className="alert alert-info m-t20" role="alert">
                                                    <i className="fa fa-info-circle m-r5"></i>
                                                    Las empresas no pueden postularse a vacantes.
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {suggestedVacancies.length > 0 && (
                        <div className="section-full content-inner" style={{ marginTop: 50, backgroundColor: '#f8f8f8', }}>
                            <div className="container" style={{ borderRadius: 10 }}>
                                <div className="section-head text-center" style={{ paddingTop: 10 }}>
                                    <h2 className="m-b10" style={{ color: '#00004C' }}>Vacantes Relacionadas</h2>
                                    <p>Otras vacantes que podrían interesarte</p>
                                </div>
                                {loadingSuggestions ? (
                                    <div className="text-center p-t50 p-b50">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="sr-only">Cargando sugerencias...</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{
                                        display: 'flex',
                                        overflowX: 'auto',
                                        overflowY: 'hidden',
                                        gap: '20px',
                                        paddingBottom: '10px',
                                        scrollbarWidth: 'thin',
                                        scrollbarColor: '#009247 #f5f5f5'
                                    }}>
                                        {suggestedVacancies.map((suggestedVacancy) => {
                                            const suggestedLocation = `${getRelationName(suggestedVacancy.ciudad)}, ${getRelationName(suggestedVacancy.pais)}`;
                                            const suggestedSalary = formatSalary(suggestedVacancy.salarioMinimo, suggestedVacancy.salarioMaximo);
                                            const suggestedModalidad = getRelationName(suggestedVacancy.modalidadTrabajo);

                                            return (
                                                <div key={suggestedVacancy.id} style={{
                                                    minWidth: '300px',
                                                    maxWidth: '300px',
                                                    flexShrink: 0,
                                                    backgroundColor: '#fff'
                                                }}>
                                                    <div className="m-b30 blog-grid">
                                                        <div className="dez-info p-a20 border-1" style={{
                                                            border: '1px solid #e8e8e8',
                                                            borderRadius: '8px',
                                                            transition: 'all 0.3s ease',
                                                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                                            height: '100%',
                                                            display: 'flex',
                                                            flexDirection: 'column'
                                                        }}>
                                                            <div className="dez-post-title">
                                                                <h5 className="post-title">
                                                                    <Link to={`/job-detail?id=${suggestedVacancy.id}`} style={{
                                                                        color: '#333',
                                                                        textDecoration: 'none',
                                                                        transition: 'color 0.3s ease'
                                                                    }}
                                                                        onMouseEnter={(e) => e.target.style.color = '#009247'}
                                                                        onMouseLeave={(e) => e.target.style.color = '#333'}>
                                                                        {suggestedVacancy.titulo}
                                                                    </Link>
                                                                </h5>
                                                            </div>
                                                            <div className="dez-post-meta">
                                                                <ul>
                                                                    <li className="post-date">
                                                                        <i className="ti-location-pin"></i> {suggestedLocation}
                                                                    </li>
                                                                    <li className="post-date">
                                                                        <i className="ti-bookmark"></i> {suggestedModalidad}
                                                                    </li>
                                                                    <li className="post-date">
                                                                        <i className="ti-money"></i> {suggestedSalary}
                                                                    </li>
                                                                </ul>
                                                            </div>
                                                            {suggestedVacancy.descripcion && (
                                                                <div className="dez-post-text" style={{ flexGrow: 1 }}>
                                                                    <div
                                                                        dangerouslySetInnerHTML={{ __html: suggestedVacancy.descripcion.substring(0, 150) + (suggestedVacancy.descripcion.length > 150 ? '...' : '') }}
                                                                        style={{
                                                                            color: '#666',
                                                                            fontSize: '14px',
                                                                            lineHeight: '1.6'
                                                                        }}
                                                                    />
                                                                </div>
                                                            )}
                                                            <div className="dez-post-readmore">
                                                                <Link
                                                                    to={`/job-detail?id=${suggestedVacancy.id}`}
                                                                    title="VER DETALLES"
                                                                    rel="bookmark"
                                                                    className="site-button-link"
                                                                >
                                                                    <span className="fw6">VER DETALLES</span>
                                                                </Link>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
